import type { Capability, MembershipsResponse, Scope } from '@/api/pending'
import type { IconName } from '@/ui'

export type SectionId =
  'overview' | 'approvals' | 'bookings' | 'desk' | 'residents' | 'condo' | 'balances' | 'staff'

export interface Section {
  id: SectionId
  /** Path under /c/:condoId. */
  path: string
  /** Other paths that belong to this section (nested screens). */
  extraPaths?: string[]
  label: string
  icon: IconName
  capability: Capability
}

/** Single source of truth: capability → route → nav item. Order is the nav order. */
export const SECTIONS: readonly Section[] = [
  {
    id: 'overview',
    path: '/overview',
    label: 'Visão geral',
    icon: 'dashboard',
    capability: 'overview',
  },
  {
    id: 'approvals',
    path: '/approvals',
    label: 'Aprovações',
    icon: 'flag',
    capability: 'moderation',
  },
  {
    id: 'bookings',
    path: '/bookings',
    label: 'Reservas',
    icon: 'calendar_month',
    capability: 'bookings_review',
  },
  { id: 'desk', path: '/desk', label: 'Portaria', icon: 'move_to_inbox', capability: 'desk' },
  {
    id: 'residents',
    path: '/residents',
    label: 'Moradores e unidades',
    icon: 'group',
    capability: 'residents',
  },
  { id: 'condo', path: '/condo', label: 'Condomínio', icon: 'apartment', capability: 'condo_info' },
  {
    id: 'balances',
    path: '/balances',
    label: 'Balancetes',
    icon: 'payments',
    capability: 'balances',
  },
  {
    id: 'staff',
    path: '/staff',
    extraPaths: ['/staff/permissions'],
    label: 'Equipe e permissões',
    icon: 'key',
    capability: 'team',
  },
]

export const ALL_CAPABILITIES: readonly Capability[] = SECTIONS.map((s) => s.capability)

/** Sections a capability set can open. `features` (VITE_FEATURES) can hide sections whose API isn't live. */
export function allowedSections(
  capabilities: ReadonlySet<Capability>,
  features: ReadonlySet<SectionId> | null = null,
): Section[] {
  return SECTIONS.filter(
    (s) => capabilities.has(s.capability) && (features === null || features.has(s.id)),
  )
}

export function firstAllowedPath(
  condoId: number,
  capabilities: ReadonlySet<Capability>,
  features: ReadonlySet<SectionId> | null = null,
): string | null {
  const first = allowedSections(capabilities, features)[0]
  return first ? `/c/${condoId}${first.path}` : null
}

/** Section owning a path under /c/:condoId (e.g. `/staff/permissions` → staff). */
export function sectionForPath(path: string): Section | undefined {
  return SECTIONS.find((s) => [s.path, ...(s.extraPaths ?? [])].includes(path.replace(/\/$/, '')))
}

/** Root sees every capability in every condominium. */
export function capabilitiesFor(me: MembershipsResponse, condoId: number): Set<Capability> {
  if (me.is_root) return new Set(ALL_CAPABILITIES)
  const m = me.memberships.find((x) => x.condominium_id === condoId)
  return new Set(m?.capabilities ?? [])
}

/** Condominiums the person can manage: at least one capability, or all of them for root. */
export function managerContexts(me: MembershipsResponse) {
  return me.memberships.filter((m) => me.is_root || m.capabilities.length > 0)
}

const ROLE_LABEL: Record<Scope, string> = {
  admin: 'Administrador',
  reviewer: 'Revisador',
  doorman: 'Porteiro',
  resident: 'Morador',
}

/** "Administrador", "Revisador · Porteiro"... Root shows as Root. */
export function roleLabel(scopes: Scope[], isRoot: boolean): string {
  if (isRoot) return 'Root'
  const order: Scope[] = ['admin', 'reviewer', 'doorman']
  const held = order.filter((s) => scopes.includes(s))
  return held.length ? held.map((s) => ROLE_LABEL[s]).join(' · ') : ROLE_LABEL.resident
}
